<template>
  <div class="container-fluid bg-black text-light py-4 min-vh-100">
    <!-- ... header unchanged ... -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <!-- Left: title -->
      <div class="h4 mb-0 text-white">
        📈 Opened Positions ({{ openedPositions.length }})
      </div>

      <!-- Right: bot + telegram + metrics -->
      <div class="d-flex flex-column align-items-end">
        <!-- Ligne du haut : Bot selector + Telegram + metrics (DESKTOP uniquement) -->
        <div class="d-flex align-items-center gap-3">
          <!-- Bot selector -->
          <select
              id="botSelector"
              class="form-select form-select-sm d-inline-block w-auto"
              v-model="selectedSource"
          >
            <!-- All bots / no filter -->
            <option value="">All bots</option>

            <!-- One entry per source -->
            <option
                v-for="src in availableSources"
                :key="src"
                :value="src"
            >
              {{ src }}
            </option>
          </select>


          <!-- Telegram switch -->
          <div class="form-check form-switch text-white">
            <input
                class="form-check-input"
                type="checkbox"
                id="telegramSwitch"
                v-model="telegramEnabled"
                @change="handleTelegramToggle"
                :disabled="telegramToggleLoading || !selectedSource"
            >
            <label class="form-check-label" for="telegramSwitch">
              Telegram
            </label>
          </div>

          <!-- Metrics version DESKTOP (>= md) -->
          <div class="d-none d-md-flex gap-3">
        <span class="text-white">
          ETV: <span class="text-success">{{ format(etv) }}</span>
        </span>
            <span class="text-white">
          AV: <span class="text-success">{{ format(av) }}</span>
        </span>
            <span class="text-white">
          Total PnL:
          <span :class="totalOpenedPnl >= 0 ? 'text-success' : 'text-danger'">
            {{ format(totalOpenedPnl) }}
          </span>
        </span>
          </div>
        </div>

        <!-- Ligne du bas : Metrics version MOBILE (< md) -->
        <div class="d-flex gap-3 mt-1 d-flex d-md-none">
      <span class="text-white">
        ETV: <span class="text-success">{{ format(etv) }}</span>
      </span>
          <span class="text-white">
        AV: <span class="text-success">{{ format(av) }}</span>
      </span>
          <span class="text-white">
        Total PnL:
        <span :class="totalOpenedPnl >= 0 ? 'text-success' : 'text-danger'">
          {{ format(totalOpenedPnl) }}
        </span>
      </span>
        </div>
      </div>
    </div>

    <!-- 🔹 Opened Positions Table -->
    <div class="card bg-dark text-white mb-4">
      <div class="card-body p-0">
        <div class="table-container">
          <table class="table table-dark table-hover table-sm mb-0">
            <thead class="sticky-top bg-secondary text-white">
            <tr>
              <th v-if="showSourceColumn">Source</th>
              <th>Symbol</th>
              <th>Direction</th>
              <th>Lev.</th>
              <th>Margin</th>
              <th>Size</th>
              <th>Entry</th>
              <th>Mark</th>
              <th>Next DD</th>
              <th>ROI</th>
              <th>PNL</th>
              <th>Entries</th>
              <th>Entry TS</th>
              <th>Liq</th>
              <th>Risk</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="pos in sortedOpenedPositions" :key="`group-open-${pos.id}`">
              <tr @click="openDetails(pos)" style="cursor: pointer;">
                <td v-if="showSourceColumn">{{ pos.source }}</td>
                <td>{{ pos.symbol }}</td>
                <td>{{ pos.direction }}</td>
                <td>x{{ formatInt(pos.leverage) }}</td>
                <td>{{ format(pos.total_margin) }}</td>
                <td>{{ formatSize(pos.size) }}</td>
                <td>{{ formatPrice(pos.entryPrice) }}</td>
                <td>{{ formatPrice(pos.markPrice) }}</td>
                <td>{{ formatNextDd(pos) }}</td>
                <td>{{ format(pos.roi) }}%</td>
                <td :class="{ 'text-success': pos.pnl >= 0, 'text-danger': pos.pnl < 0 }">{{ format(pos.pnl) }}</td>
                <td>{{ pos.entry_count }}</td>
                <td>{{ formatDate(pos.entry_ts) }}</td>
                <td>{{ formatPrice(pos.liq_price) }}</td>
                <td>
                  <span
                      v-if="getLiveRisk(pos) !== null"
                      :class="riskClass(getLiveRisk(pos))"
                  >
                    {{ formatRisk(getLiveRisk(pos)) }}%
                  </span>
                  <span v-else>—</span>
                </td>
              </tr>
              <template v-if="ordersByPosition[pos.id] && ordersByPosition[pos.id].length > 1 || 1">
                <template v-for="ord in ordersByPosition[pos.id]" :key="'ord-open-' + ord.id">
                  <tr class="bg-secondary bg-opacity-25">
                    <td v-if="showSourceColumn"></td>
                    <td colspan="2" class="ps-4">↳ Order</td>
                    <td></td>
                    <td>{{ format(ord.margin) }}</td>
                    <td>{{ formatSize(ord.qty) }}</td>
                    <td>{{ formatPrice(ord.price) }}</td>
                    <td colspan="5"></td>
                    <td>{{ formatDate(ord.ts) }}</td>
                    <td colspan="2"></td>
                  </tr>
                </template>
              </template>
            </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>


    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="h5 mb-0">📉 Closed Positions ({{ filteredClosedPositions.length }})</div>
      <div class="d-flex align-items-center ms-auto gap-4">
        <input v-model="searchQuery" type="text" placeholder="Search..."
               class="form-control bg-dark text-white w-auto" style="min-width: 200px;" />
        <span>
      Total PnL:
      <span :class="{ 'text-success': totalClosedPnl >= 0, 'text-danger': totalClosedPnl < 0 }">
        {{ totalClosedPnl }}
      </span>
    </span>
      </div>
    </div>

    <!-- 🔻 Closed Positions Table -->
    <div class="card bg-dark text-white mb-4">
      <div class="card-body p-0">
        <div class="table-container">
          <table class="table table-dark table-hover table-sm mb-0">
            <thead class="sticky-top bg-secondary text-white">
            <tr>
              <th v-if="showSourceColumn">Source</th>
              <th>Symbol</th>
              <th>Direction</th>
              <th>Lev.</th>
              <th>Margin</th>
              <th>Size</th>
              <th>Entry</th>
              <th>Mark</th>
              <th>ROI</th>
              <th>PNL</th>
              <th>Comm</th>
              <th>Fees</th>
              <th>Final PNL</th>
              <th>Entries</th>
              <th>Entry TS</th>
              <th>Exit TS</th>
              <th>Lifetime</th>
              <th>Risk</th>
            </tr>
            </thead>
            <tbody>
            <template v-for="pos in sortedClosedPositions" :key="`group-close-${pos.id}`">
              <tr @click="openDetails(pos)" style="cursor: pointer;">
                <td v-if="showSourceColumn">{{ pos.source }}</td>
                <td>{{ pos.symbol }}</td>
                <td>{{ pos.direction }}</td>
                <td>x{{ formatInt(pos.leverage) }}</td>
                <td>{{ format(pos.total_margin) }}</td>
                <td>{{ formatSize(pos.size) }}</td>
                <td>{{ formatPrice(pos.entryPrice) }}</td>
                <td>{{ formatPrice(pos.markPrice) }}</td>
                <td>{{ format(pos.roi) }}%</td>
                <td :class="{ 'text-success': pos.pnl >= 0, 'text-danger': pos.pnl < 0 }">{{ format(pos.pnl) }}</td>
                <td
                    :class="{
                            'text-success': pos.commission < 0,
                            'text-danger': pos.commission > 0
                          }"
                >
                  {{ pos.commission > 0 ? '-' + format(pos.commission) : format(pos.commission) }}
                </td>
                <td :class="{ 'text-success': pos.funding_fee > 0, 'text-danger': pos.funding_fee < 0 }">{{ format(pos.funding_fee) }}</td>
                <td :class="{ 'text-success': pos.pnl_net >= 0, 'text-danger': pos.pnl_net < 0 }">{{ format(pos.pnl_net) }}</td>
                <td>{{ pos.entry_count }}</td>
                <td>{{ formatDate(pos.entry_ts) }}</td>
                <td>{{ formatDate(pos.exit_ts) }}</td>
                <td>{{ formatLifetime(pos.entry_ts, pos.exit_ts) }}</td>
                <td>
                  <span
                      v-if="getMaxRisk(pos) !== null"
                      :class="riskClass(getMaxRisk(pos))"
                  >
                    {{ formatRisk(getMaxRisk(pos)) }}%
                  </span>
                  <span v-else>—</span>
                </td>
              </tr>
              <template v-if="ordersByPosition[pos.id] && ordersByPosition[pos.id].length > 1 || 1">
                <template v-for="ord in ordersByPosition[pos.id]" :key="'ord-close-' + ord.id">
                  <tr class="bg-secondary bg-opacity-25">
                    <td v-if="showSourceColumn"></td>
                    <td colspan="2" class="ps-4">↳ Order</td>
                    <td></td>
                    <td>{{ format(ord.margin) }}</td>
                    <td>{{ formatSize(ord.qty) }}</td>
                    <td>{{ formatPrice(ord.price) }}</td>
                    <td colspan="7"></td>
                    <td>{{ formatDate(ord.ts) }}</td>
                    <td colspan="3"></td>
                  </tr>
                </template>
              </template>
            </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 🔍 Détail d'une position sélectionnée -->
    <div v-if="selectedPosition" class="modal-backdrop" @click.self="selectedPosition = null">
      <div class="modal-content">
        <h5 class="mb-3">📌 Position #{{ selectedPosition.id }} – {{ selectedPosition.symbol }} ({{ selectedPosition.direction }})</h5>
        <p class="mb-2"><strong>Levier :</strong> x{{ selectedPosition.leverage }}</p>
        <p><strong>Margin :</strong> {{ format(selectedPosition.total_margin) }} – <strong>ROI :</strong> {{ format(selectedPosition.roi) }}%</p>

        <hr />

        <h6 class="mt-3">📈 Market Data Snapshots :</h6>
        <table class="table table-sm table-striped">
          <thead>
          <tr>
            <th>#</th>
            <th>🕒 Timestamp</th>
            <th>OpenInterest</th>
            <th>TakerRatio</th>
            <th>BuyVol</th>
            <th>SellVol</th>
            <th>AccountRatio</th>
            <th>Long</th>
            <th>Short</th>
            <th>PositionRatio</th>
            <th>Long</th>
            <th>Short</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(snap, i) in parsedMarketDataSnapshots" :key="'md-' + i">
            <td>{{ i + 1 }}</td>
            <td>{{ formatDateTS(snap.ts) }}</td>
            <td>{{ format(snap.openInterest) }}</td>
            <td>{{ format(snap.takerLongShort?.buySellRatio) }}</td>
            <td>{{ format(snap.takerLongShort?.buyVol) }}</td>
            <td>{{ format(snap.takerLongShort?.sellVol) }}</td>
            <td>{{ format(snap.accountLongShort?.longShortRatio) }}</td>
            <td>{{ format(snap.accountLongShort?.longAccount) }}</td>
            <td>{{ format(snap.accountLongShort?.shortAccount) }}</td>
            <td>{{ format(snap.positionLongShort?.longShortRatio) }}</td>
            <td>{{ format(snap.positionLongShort?.longAccount) }}</td>
            <td>{{ format(snap.positionLongShort?.shortAccount) }}</td>
          </tr>
          </tbody>
        </table>

        <h6 class="mt-3">📘 Snapshots OrderBook :</h6>
        <table class="table table-sm table-striped">
          <thead><tr><th>#</th><th>ts</th><th>midPrice</th><th>imbalance</th><th>bidVol</th><th>askVol</th></tr></thead>
          <tbody>
          <tr v-for="(snap, i) in parsedSnapshots" :key="'ob-' + i">
            <td>{{ i + 1 }}</td>
            <td>{{ formatDate(snap.ts) }}</td>
            <td>{{ formatPrice(snap.midPrice) }}</td>
            <td>{{ format(snap.imbalance) }}</td>
            <td>{{ format(snap.bidVolume) }}</td>
            <td>{{ format(snap.askVolume) }}</td>
          </tr>
          </tbody>
        </table>

        <h6 class="mt-3">📊 Indicateurs :</h6>
        <ul class="list-group">
          <li class="list-group-item" v-for="(val, key) in parsedIndicators" :key="key">
            <strong>{{ key }}:&nbsp;</strong>
            <span v-if="typeof val === 'object'">{{ JSON.stringify(val) }}</span>
            <span v-else>{{ val }}</span>
          </li>
        </ul>

        <button class="btn btn-outline-secondary mt-4" @click="selectedPosition = null">Fermer</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import axios from 'axios'
import {
  positionsSLFactory as positionsSL,
  ordersSLFactory as ordersSL,
  binanceSLSummaryFactory as binanceSLSummary
} from '@/state'

const VITE_REST_URL = import.meta.env.VITE_REST_URL || ''

// 🔹 Champs affichés
const headers = [
  'symbol', 'direction', 'leverage', 'total_margin', 'size',
  'entryPrice', 'markPrice', 'roi', 'pnl', 'entry_count', 'entry_ts', 'exit_ts'
]

// 🔹 Champs d’état local
const searchQuery = ref('')
const expandedOrders = ref({})
const selectedPosition = ref(null)

// 🔹 Telegram toggle state
const telegramEnabled = ref(true)
const telegramToggleLoading = ref(false)


const selectedSource = ref(""); // "" = all bots

const availableSources = computed(() => {
  const summary = binanceSLSummary.value || {};
  return Object.keys(summary);
});

const showSourceColumn = computed(() => !selectedSource.value);

// Persist last selected bot for Martingale page
const STORAGE_KEY_SELECTED_SOURCE = 'factory.selectedSource';

// Restore selection on mount (default: All bots)
onMounted(() => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_SELECTED_SOURCE);
    if (saved !== null) {
      // saved can be '' meaning "All bots"
      selectedSource.value = saved;
    }
  } catch (err) {
    console.error('[Martingale] Failed to read localStorage:', err?.message, err?.stack);
  }
});

// Persist selection whenever it changes
watch(selectedSource, (val) => {
  try {
    localStorage.setItem(STORAGE_KEY_SELECTED_SOURCE, val ?? '');
  } catch (err) {
    console.error('[Martingale] Failed to write localStorage:', err?.message, err?.stack);
  }

  // Existing behavior
  fetchTelegramEnabled();
});

// If saved bot no longer exists, fallback to "All bots"
watch(availableSources, (sources) => {
  if (!selectedSource.value) return;
  if (!sources.includes(selectedSource.value)) {
    selectedSource.value = '';
  }
});


// Reload Telegram flag when selected bot changes
watch(selectedSource, () => {
  fetchTelegramEnabled();
});

/**
 * Fetch TELEGRAM_ENABLED from parameters API.
 */
const fetchTelegramEnabled = async () => {
  // If no specific bot is selected ("All bots"), we disable the switch and skip the request
  if (!selectedSource.value) {
    telegramEnabled.value = false;
    return;
  }

  try {
    const res = await axios.get(`${VITE_REST_URL}/parameters/prescient`, {
      params: {
        source: selectedSource.value,
        names: 'TELEGRAM_ENABLED'
      }
    });
    const params = Array.isArray(res.data) ? res.data : [];
    const tgParam = params.find(p => p.name === 'TELEGRAM_ENABLED');

    if (tgParam && tgParam.value != null) {
      telegramEnabled.value = String(tgParam.value).toLowerCase() === 'true';
    } else {
      // Default to false if parameter does not exist yet for this bot
      telegramEnabled.value = false;
    }
  } catch (err) {
    console.error(
        '❌ Error fetching TELEGRAM_ENABLED:',
        err.message,
        err.stack
    );
    // In case of error, keep switch off to avoid misleading state
    telegramEnabled.value = false;
  }
};


/**
 * Toggle TELEGRAM_ENABLED in DB when user flips the switch.
 */
const handleTelegramToggle = async () => {
  // Safety guard: if "All bots" is selected, do nothing
  if (!selectedSource.value) {
    return;
  }

  const newValue = telegramEnabled.value;
  telegramToggleLoading.value = true;

  try {
    await axios.post(`${VITE_REST_URL}/parameters`, {
      source: selectedSource.value,
      name: 'TELEGRAM_ENABLED',
      value: newValue ? 'true' : 'false'
    });
  } catch (err) {
    console.error(
        '❌ Error updating TELEGRAM_ENABLED:',
        err.message,
        err.stack
    );
    // Revert UI in case of failure
    telegramEnabled.value = !newValue;
  } finally {
    telegramToggleLoading.value = false;
  }
};


onMounted(fetchTelegramEnabled)

const parsedMarketData = computed(() => {
  if (!selectedPosition.value?.market_snapshot) return {};
  try {
    return typeof selectedPosition.value.market_snapshot === 'string'
        ? JSON.parse(selectedPosition.value.market_snapshot)
        : selectedPosition.value.market_snapshot;
  } catch (e) {
    console.error('❌ Erreur parsing marketData:', e);
    return {};
  }
});

const parsedMarketDataSnapshots = computed(() => {
  const data = selectedPosition.value?.market_snapshot;
  if (!data) return [];
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return Array.isArray(parsed) ? parsed : [parsed]; // handle older positions with single snapshot
  } catch (e) {
    console.error("❌ Erreur parsing market_snapshot:", e);
    return [];
  }
});


// 🔹 Parsing snapshots avec protection
const parsedSnapshots = computed(() => {
  const raw = selectedPosition.value?.orderbook_snapshot;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw || [];
  } catch (e) {
    console.warn('❌ Snapshots mal formés:', e);
    return [];
  }
});

const parsedIndicators = computed(() => {
  const raw = selectedPosition.value?.indicators_snapshot;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw || {};
  } catch (e) {
    console.warn('❌ Indicateurs mal formés:', e);
    return {};
  }
})

// 🔹 Méthodes de popup
function openDetails(pos) {
  selectedPosition.value = pos
}
function closeDetails() {
  selectedPosition.value = null
}

const positionsForSelectedSource = computed(() => {
  if (!selectedSource.value) return positionsSL.value;
  return positionsSL.value.filter(p => p.source === selectedSource.value);
});

const openedPositions = computed(() =>
    positionsForSelectedSource.value.filter(p => p.status === 'open')
);

const closedPositions = computed(() =>
    positionsForSelectedSource.value.filter(p => p.status === 'closed')
);


const sortedOpenedPositions = computed(() =>
    [...openedPositions.value].sort((a, b) => new Date(b.entry_ts) - new Date(a.entry_ts))
)

const filteredClosedPositions = computed(() =>
    closedPositions.value
        .filter(p =>
            JSON.stringify(p).toLowerCase().includes(searchQuery.value.toLowerCase())
        )
        .map(p => ({
          ...p,
          lifetime: formatLifetime(p.entry_ts, p.exit_ts)
        }))
)

const sortedClosedPositions = computed(() =>
    [...filteredClosedPositions.value].sort((a, b) => new Date(b.exit_ts) - new Date(a.exit_ts))
)

const totalOpenedPnl = computed(() =>
    openedPositions.value.reduce((sum, p) => sum + (Number(p.pnl) || 0), 0).toFixed(2)
)
const totalClosedPnlBrut = computed(() =>
    closedPositions.value.reduce((sum, p) => sum + (Number(p.pnl) || 0), 0).toFixed(2)
)
const totalClosedPnl = computed(() =>
    closedPositions.value.reduce((sum, p) => sum + (Number(p.pnl_net) || 0), 0).toFixed(2)
)

const ordersByPosition = computed(() =>
    ordersSL.value.reduce((acc, order) => {
      if (!acc[order.position_id]) acc[order.position_id] = []
      acc[order.position_id].push(order)
      return acc
    }, {})
)

// Aggregated summary over all bots (used when selectedSource is empty)
const aggregatedSummary = computed(() => {
  const all = binanceSLSummary.value || {};
  let totalETV = 0;
  let totalAV = 0;
  let latestTS = null;

  for (const key in all) {
    const row = all[key];
    if (!row) continue;

    const etvNum = Number(row.estimated_total_value || 0);
    const avNum = Number(row.available_balance || 0);
    totalETV += isNaN(etvNum) ? 0 : etvNum;
    totalAV += isNaN(avNum) ? 0 : avNum;

    if (!latestTS || (row.timestamp && row.timestamp > latestTS)) {
      latestTS = row.timestamp;
    }
  }

  return {
    estimated_total_value: totalETV,
    available_balance: totalAV,
    timestamp: latestTS
  };
});

// Current summary depends on selectedSource
const currentSummary = computed(() => {
  const all = binanceSLSummary.value || {};

  if (selectedSource.value && all[selectedSource.value]) {
    return all[selectedSource.value];
  }

  // No source selected -> use aggregated summary
  return aggregatedSummary.value;
});

const etv = computed(() =>
    currentSummary.value?.estimated_total_value
        ? Number(currentSummary.value.estimated_total_value)
        : 0
);

const av = computed(() =>
    currentSummary.value?.available_balance
        ? Number(currentSummary.value.available_balance)
        : 0
);


// 🔹 Regrouper une position et ses ordres
function renderRow(position) {
  const orders = ordersByPosition.value[position.id] || []
  return [position, ...orders.map(order => ({ ...order, isOrder: true }))]
}

// 🔹 Format durée
function formatLifetime(entry_ts, exit_ts) {
  if (!entry_ts || !exit_ts) return ""
  const start = new Date(entry_ts)
  const end = new Date(exit_ts)
  const diffMs = end - start
  const mins = Math.floor(diffMs / 60000)
  const hrs = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return `${hrs}h ${remainingMins}m`
}

// 🔹 Helpers
const format = val => Number(val).toFixed(2)
const formatInt = val => parseInt(val)
const formatSize = val => Number(val).toFixed(4)
const formatPrice = val => Number(val).toFixed(6)

// --- Next DD computation (mirror prescientMartingale_core_opt2.js logic) ---
// Keep code & comments in English
// IMPORTANT: keep this table strictly in sync with the backend core.
// Backend (prescientMartingale_core_opt2.js): DOUBLEDOWN_TRIGGER_ROIS = [-150, -275, -425, -600]
//const DD_TRIGGER_ROIS = [-150, -300, -500, -750];
const DD_TRIGGER_ROIS = [-150, -250, -350, -450];
const DD_MAX_NUMBER = DD_TRIGGER_ROIS.length;

// Swiss-style thousands with apostrophe: 90000 -> "90'000"
function formatThousandsApostrophe(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  const rounded = Math.round(num);
  return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}

// Compute the next DD trigger ROI (negative) from current ddCount.
// ddCount is the number of DD already executed (extra entries beyond the first one).
// Next trigger index = ddCount.
function computeNextDdTriggerRoi(ddCount) {
  const idx = Number(ddCount);
  if (!Number.isFinite(idx) || idx < 0) return null;
  return DD_TRIGGER_ROIS[idx] ?? null;
}

// Convert a ROI% threshold into a price level from entryPrice/leverage/direction
function computePriceFromRoi(entryPrice, leverage, direction, roiPct) {
  const entry = Number(entryPrice);
  const lev = Number(leverage);
  const roi = Number(roiPct);

  if (!Number.isFinite(entry) || entry <= 0) return null;
  if (!Number.isFinite(lev) || lev <= 0) return null;
  if (!Number.isFinite(roi)) return null;

  const ratio = (roi / 100) / lev;

  if (direction === "LONG") {
    return entry * (1 + ratio);
  }
  if (direction === "SHORT") {
    return entry * (1 - ratio);
  }
  return null;
}

// Public formatter used in the table
function formatNextDd(pos) {
  try {
    const orders = ordersByPosition.value?.[pos.id] || [];
    const entryCount = Array.isArray(orders) ? orders.length : Number(pos.entry_count || 0);

    // ddCount = number of extra entries beyond the first one
    const ddCount = Math.max(0, entryCount - 1);

    // No "next" DD once we are already at cap
    if (ddCount >= DD_MAX_NUMBER) return "—";

    const triggerRoi = computeNextDdTriggerRoi(ddCount);
    if (triggerRoi == null) return "—";
    const nextPrice = computePriceFromRoi(pos.entryPrice, pos.leverage, pos.direction, triggerRoi);

    if (!Number.isFinite(nextPrice) || nextPrice <= 0) return "—";

    // Display: 90'000 (-450%)
    const roiLabel = Number.isFinite(triggerRoi) ? `${Math.round(triggerRoi)}%` : "—";
    return `${formatThousandsApostrophe(nextPrice)} (${roiLabel})`;
  } catch (err) {
    console.error('[Martingale] formatNextDd failed:', err?.message, err?.stack);
    return "—";
  }
}

function formatDate(val) {
  try {
    const date = val instanceof Date ? val : new Date(val);
    const localDate = new Date(date.getTime());
    const hrs = localDate.getHours().toString().padStart(2, "0");
    const mins = localDate.getMinutes().toString().padStart(2, "0");
    const secs = localDate.getSeconds().toString().padStart(2, "0");
    const day = localDate.getDate().toString().padStart(2, "0");
    const month = (localDate.getMonth() + 1).toString().padStart(2, "0");
    const year = localDate.getFullYear();

    return `${day}/${month}/${year} ${hrs}:${mins}:${secs}`;
  } catch (err) {
    console.error("❌ Erreur formatDate:", err.message);
    return "Invalid Date";
  }
}

function formatDateTS(ts) {
  try {
    if (typeof ts !== "number") return "??";
    const date = new Date(ts);
    if (isNaN(date)) return "Invalid Date";
    return date.toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  } catch (err) {
    console.error("❌ Erreur formatDateTS:", err.message, err.stack);
    return "Error";
  }
}



// Compute risk based on mark and liquidation prices
function computeRisk(markPrice, liqPrice, direction) {
  const mark = Number(markPrice);
  const liq = Number(liqPrice);

  if (!Number.isFinite(mark) || !Number.isFinite(liq) || mark <= 0 || liq <= 0) {
    return null;
  }

  if (direction === 'LONG') {
    return (liq / mark) * 100;
  }
  if (direction === 'SHORT') {
    return (mark / liq) * 100;
  }
  return null;
}

// Live risk for opened positions
function getLiveRisk(pos) {
  return computeRisk(pos.markPrice, pos.liq_price, pos.direction);
}

// Worst risk reached for closed positions
function getMaxRisk(pos) {
  return computeRisk(pos.max_risk_mark_price, pos.max_risk_liq_price, pos.direction);
}

// Format risk as string or fallback
function formatRisk(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

// Optional: colorize risk level
function riskClass(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '';
  if (n >= 90) return 'text-danger fw-bold';   // very high risk
  if (n >= 75) return 'text-warning';          // medium risk
  return 'text-success';                       // low risk
}

</script>




<style scoped>
.order-row {
  background-color: #1f2937;
  margin-left: 1rem;
}
table {
  font-size: 0.75rem; /* text-xs */
}

.table-container {
  max-height: 70vh;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #2c2c2c !important;
}

.table {
  color: white;
  background-color: #1f1f1f;
}

.table thead {
  background-color: #333;
}

.modal-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #1e1e2f;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-height: 90%;
  overflow-y: auto;
  color: white;
}
</style>
